import { projectForReaders } from './connectedBible'
import type { StoryProject } from './types'
import { describeCondition, describeEffect, displayValue } from './storyLogic'
import { slugify } from './model'
import { downloadBlob } from './download'

export type ReviewOptions = {
  nodeIds?: string[]
  includeLogic?: boolean
  newPagePerScene?: boolean
}
export type ReviewParagraph = {
  role:
    | 'title'
    | 'subtitle'
    | 'scene'
    | 'chapter'
    | 'cue'
    | 'direction'
    | 'action'
    | 'dialogue'
    | 'route-heading'
    | 'choice'
    | 'logic'
    | 'ending'
  text: string
  pageBreak?: boolean
}

export function reviewParagraphs(
  project: StoryProject,
  options: ReviewOptions = {},
): ReviewParagraph[] {
  project = projectForReaders(project)
  const nodes = project.nodes.filter(
    (node) => !options.nodeIds || options.nodeIds.includes(node.id),
  )
  const variables = project.variables ?? []
  const paragraphs: ReviewParagraph[] = [
    { role: 'title', text: project.title || 'Untitled story' },
    {
      role: 'subtitle',
      text: `Screenplay review draft\n${nodes.length} ${nodes.length === 1 ? 'scene' : 'scenes'} with action, dialogue, and player choices.`,
    },
  ]
  const destination = (id: string | null) => {
    const index = project.nodes.findIndex((node) => node.id === id)
    return index < 0
      ? '[Unconnected route]'
      : `Scene ${index + 1}  ${project.nodes[index].title}`
  }
  if (options.includeLogic && variables.length) {
    paragraphs.push({ role: 'route-heading', text: 'Story starting values' })
    variables.forEach((variable) =>
      paragraphs.push({
        role: 'logic',
        text: `${variable.name}: ${displayValue(variable.initialValue)}`,
      }),
    )
  }
  nodes.forEach((node, index) => {
    paragraphs.push({
      role: 'scene',
      text: `Scene ${project.nodes.indexOf(node) + 1}  ${node.title || 'Untitled scene'}`,
      pageBreak: Boolean(options.newPagePerScene && index > 0),
    })
    if (node.chapter) paragraphs.push({ role: 'chapter', text: node.chapter })
    if (node.id === project.startNodeId)
      paragraphs.push({ role: 'chapter', text: 'Opening scene' })
    if (options.includeLogic && node.effects?.length)
      paragraphs.push({
        role: 'logic',
        text:
          'On entry: ' +
          node.effects
            .map((effect) => describeEffect(effect, variables))
            .join('; '),
      })
    node.blocks.forEach((block) => {
      if (block.kind === 'dialogue') {
        paragraphs.push({
          role: 'cue',
          text: (block.speaker || 'CHARACTER').toUpperCase(),
        })
        if (block.direction)
          paragraphs.push({
            role: 'direction',
            text: '(' + block.direction + ')',
          })
      }
      paragraphs.push({
        role: block.kind === 'dialogue' ? 'dialogue' : 'action',
        text: block.text || '[Unwritten line]',
      })
    })
    if (node.kind === 'ending')
      paragraphs.push({ role: 'ending', text: 'THE END' })
    else if (node.choices.length) {
      paragraphs.push({ role: 'route-heading', text: 'Player choices' })
      node.choices.forEach((choice, choiceIndex) => {
        paragraphs.push({
          role: 'choice',
          text: `${choiceIndex + 1}. ${choice.label || '[Untitled choice]'} → ${destination(choice.targetId)}`,
        })
        if (options.includeLogic && choice.conditions?.length)
          paragraphs.push({
            role: 'logic',
            text:
              'When ' +
              choice.conditions
                .map((condition) => describeCondition(condition, variables))
                .join(choice.conditionMode === 'any' ? ' or ' : ' and ') +
              '. Otherwise ' +
              (choice.unavailableMode === 'hide' ? 'hidden.' : 'locked.'),
          })
        if (options.includeLogic && choice.effects?.length)
          paragraphs.push({
            role: 'logic',
            text: choice.effects
              .map((effect) => describeEffect(effect, variables))
              .join('; '),
          })
      })
    } else
      paragraphs.push({
        role: 'choice',
        text: node.nextId
          ? 'Continue → ' + destination(node.nextId)
          : '[The next scene is unwritten]',
      })
  })
  return paragraphs
}

export async function reviewDocx(
  project: StoryProject,
  options: ReviewOptions = {},
): Promise<Blob> {
  const {
    Document,
    Footer,
    Paragraph,
    TextRun,
    Packer,
    HeadingLevel,
    AlignmentType,
    PageNumber,
  } = await import('docx')
  const children = reviewParagraphs(project, options).map((paragraph) => {
    const indented = ['cue', 'direction', 'dialogue'].includes(paragraph.role)
    const secondary = ['subtitle', 'chapter', 'logic'].includes(paragraph.role)
    const heading = ['scene', 'route-heading', 'cue', 'ending'].includes(
      paragraph.role,
    )
    const size =
      paragraph.role === 'title'
        ? 48
        : paragraph.role === 'scene'
          ? 32
          : secondary
            ? 21
            : paragraph.role === 'cue' || paragraph.role === 'route-heading'
              ? 22
              : 24
    return new Paragraph({
      ...(paragraph.role === 'title'
        ? { heading: HeadingLevel.TITLE }
        : paragraph.role === 'scene'
          ? { heading: HeadingLevel.HEADING_1 }
          : {}),
      children: paragraph.text.split('\n').map(
        (text, index) =>
          new TextRun({
            text,
            ...(index ? { break: 1 } : {}),
            font:
              secondary ||
              paragraph.role === 'cue' ||
              paragraph.role === 'route-heading'
                ? 'Arial'
                : 'Georgia',
            size,
            color: '000000',
            bold: heading,
            italics:
              paragraph.role === 'direction' || paragraph.role === 'logic',
          }),
      ),
      spacing: {
        before:
          paragraph.role === 'scene'
            ? 360
            : paragraph.role === 'route-heading'
              ? 180
              : 0,
        after: ['cue', 'direction', 'chapter', 'logic'].includes(paragraph.role)
          ? 80
          : 160,
        line: 300,
      },
      indent: indented ? { left: 936, right: 936 } : undefined,
      keepNext: [
        'title',
        'scene',
        'cue',
        'direction',
        'route-heading',
        'chapter',
      ].includes(paragraph.role),
      widowControl: true,
      pageBreakBefore: paragraph.pageBreak,
      alignment:
        paragraph.role === 'ending' ? AlignmentType.CENTER : AlignmentType.LEFT,
    })
  })
  const document = new Document({
    creator: 'BRAMBLE',
    title: project.title,
    description: 'Screenplay review draft',
    styles: {
      default: {
        document: {
          run: { font: 'Georgia', size: 24, color: '000000' },
          paragraph: { spacing: { after: 160, line: 300 } },
        },
        title: { run: { font: 'Georgia', size: 48, color: '000000' } },
        heading1: {
          run: { font: 'Georgia', size: 32, bold: true, color: '000000' },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { top: 1224, bottom: 1152, left: 1152, right: 1152 },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Page ',
                    font: 'Arial',
                    size: 18,
                    color: '000000',
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    font: 'Arial',
                    size: 18,
                    color: '000000',
                  }),
                ],
              }),
            ],
          }),
        },
        children,
      },
    ],
  })
  return Packer.toBlob(document)
}

export async function downloadReviewDocx(
  project: StoryProject,
  options: ReviewOptions,
) {
  const blob = await reviewDocx(project, options)
  downloadBlob(blob, slugify(project.title, 'story') + '-review.docx')
}

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        character
      ]!,
  )

export function printableReviewHtml(
  project: StoryProject,
  options: ReviewOptions = {},
) {
  const content = reviewParagraphs(project, options)
    .map((paragraph) => {
      const tag =
        paragraph.role === 'title'
          ? 'h1'
          : paragraph.role === 'scene'
            ? 'h2'
            : 'p'
      return `<${tag} class="${paragraph.role}${paragraph.pageBreak ? ' page-break' : ''}">${escapeHtml(paragraph.text).replaceAll('\n', '<br>')}</${tag}>`
    })
    .join('\n')
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${escapeHtml(project.title)} — Review copy</title><style>
  @page { size: letter portrait; margin: .85in .8in .8in; }
  * { box-sizing: border-box; } body { margin: 0; color: #000; background: #eeede7; font: 12pt/1.45 Georgia, 'Times New Roman', serif; }
  .print-tools { position: sticky; top: 0; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 24px; border-bottom: 1px solid #d3d6ca; background: #fff; font: 13px/1.6 Arial, sans-serif; }
  .print-tools button { padding: 10px 16px; color: #fff; background: #3d6345; border: 0; border-radius: 5px; font: inherit; cursor: pointer; }
  main { width: min(8.5in, 100%); min-height: 11in; margin: 24px auto; padding: .85in .8in .8in; background: #fff; box-shadow: 0 5px 25px #0001; }
  h1 { margin: 0 0 14pt; font-size: 24pt; font-weight: 400; } h2 { margin: 24pt 0 14pt; font-size: 16pt; line-height: 1.3; }
  p { margin: 0 0 10pt; white-space: normal; overflow-wrap: anywhere; widows: 2; orphans: 2; }
  .title,.scene,.chapter,.cue,.direction,.route-heading { break-after: avoid; }
  .subtitle,.chapter,.logic { font: 10.5pt/1.5 Arial, sans-serif; } .chapter,.logic { margin-bottom: 5pt; } .logic { font-style: italic; }
  .cue,.direction,.dialogue { margin-left: .65in; margin-right: .65in; } .cue { margin-bottom: 4pt; font: bold 11pt/1.4 Arial, sans-serif; } .direction { margin-bottom: 4pt; font-style: italic; }
  .route-heading { margin-top: 16pt; margin-bottom: 7pt; font: bold 11pt/1.4 Arial, sans-serif; }
  .choice { font-size: 11pt; } .ending { margin-top: 18pt; text-align: center; font-weight: bold; }
  .page-break { break-before: page; }
  @media print { body { background: #fff; } .print-tools { display: none; } main { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none; } }
  @media screen and (max-width: 600px) { main { margin: 0; padding: 28px 22px; } .print-tools { padding: 12px; font-size: 11px; } .cue,.direction,.dialogue { margin-left: 20px; margin-right: 20px; } }
  </style></head><body><nav class="print-tools"><span>Review copy · Choose Save as PDF in your browser’s print dialog.</span><button type="button" onclick="window.print()">Print / save as PDF</button></nav><main>${content}</main></body></html>`
}

export function openPrintReview(project: StoryProject, options: ReviewOptions) {
  const preview = window.open('about:blank', '_blank')
  if (!preview)
    throw new Error(
      'Allow popups for Bramble to open the print preview, then try again.',
    )
  preview.opener = null
  preview.document.open()
  preview.document.write(printableReviewHtml(project, options))
  preview.document.close()
}
